import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Sun, Sunrise, Sunset, Moon, Users, Bell } from 'lucide-react';
import { Avatar } from '../ui/Avatar';

/**
 * Welcome hero shown at the top of the logged-in home page.
 *
 * Warm cream-to-sand gradient with decorative blur orbs, time-of-day
 * greeting that adapts to the current hour, the user's name as the
 * headline, and a tasteful one-liner that surfaces the most useful
 * "what's waiting for you" stats.
 *
 * Props:
 *   userName        — string, defaults to "ברוך שובך" when empty
 *   userPhoto       — URL for the avatar (optional)
 *   zonesCount      — number
 *   newUpdatesCount — number
 *   onClick         — optional clicks the whole hero (we don't use it now)
 */

function greetingForHour(hour) {
    if (hour >= 5 && hour < 12)  return { text: 'בוקר טוב',     Icon: Sunrise };
    if (hour >= 12 && hour < 17) return { text: 'צהריים טובים',   Icon: Sun };
    if (hour >= 17 && hour < 21) return { text: 'ערב טוב',       Icon: Sunset };
    return                              { text: 'לילה טוב',      Icon: Moon };
}

function Stat({ Icon, value, label }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5 text-brand-700 shrink-0" strokeWidth={2.25} />
            <span className="font-semibold text-ink">{value}</span>
            <span>{label}</span>
        </span>
    );
}

export function WelcomeHero({ userName, userPhoto, zonesCount = 0, newUpdatesCount = 0 }) {
    const { text: greeting, Icon: GreetingIcon } = useMemo(() => greetingForHour(new Date().getHours()), []);
    const displayName = (userName && userName.trim()) ? userName.trim().split(' ')[0] : null;
    const hasStats = zonesCount > 0 || newUpdatesCount > 0;

    return (
        <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-3xl mb-6 sm:mb-8"
        >
            {/* Layered gradient background — cream → soft sand */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-50 via-surface to-secondary-50" aria-hidden="true" />

            {/* Decorative blur orbs for warmth */}
            <div className="absolute -top-16 -end-12 w-72 h-72 rounded-pill bg-brand-200/40 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-20 -start-10 w-80 h-80 rounded-pill bg-secondary-200/35 blur-3xl pointer-events-none" aria-hidden="true" />

            {/* Subtle dotted texture */}
            <svg className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" aria-hidden="true">
                <defs>
                    <pattern id="welcome-dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="2" cy="2" r="1" fill="currentColor" className="text-brand-900" />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#welcome-dots)" />
            </svg>

            {/* Soft ring outline */}
            <div className="absolute inset-0 rounded-3xl ring-1 ring-line-subtle pointer-events-none" aria-hidden="true" />

            <div className="relative px-5 sm:px-7 py-6 sm:py-8 flex items-center gap-4 sm:gap-5">
                {/* Avatar */}
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.05, ease: [0.34, 1.4, 0.64, 1] }}
                    className="relative shrink-0"
                >
                    <span
                        aria-hidden="true"
                        className="absolute inset-0 rounded-pill bg-brand-400/30 blur-md"
                    />
                    <span className="relative inline-block rounded-pill ring-4 ring-white shadow-md">
                        <Avatar
                            src={userPhoto}
                            name={userName}
                            size="xl"
                            shape="round"
                        />
                    </span>
                </motion.div>

                {/* Greeting + name + stats */}
                <div className="min-w-0 flex-1">
                    <p className="inline-flex items-center gap-1.5 text-2xs sm:text-xs font-bold uppercase tracking-wide text-brand-700/90 mb-1">
                        <GreetingIcon className="w-3.5 h-3.5" strokeWidth={2.25} />
                        <span>{greeting}</span>
                    </p>
                    <h1 className="text-2xl sm:text-3xl font-bold text-ink leading-tight truncate">
                        {displayName || 'ברוך שובך'}
                    </h1>
                    {hasStats ? (
                        <p className="mt-1.5 text-sm sm:text-base text-ink-muted leading-snug flex items-center gap-x-3 gap-y-1 flex-wrap">
                            {zonesCount > 0 && (
                                <Stat Icon={Users} value={zonesCount} label={zonesCount === 1 ? 'קהילה' : 'קהילות'} />
                            )}
                            {newUpdatesCount > 0 && (
                                <Stat Icon={Bell} value={newUpdatesCount} label={newUpdatesCount === 1 ? 'עדכון חדש' : 'עדכונים חדשים'} />
                            )}
                        </p>
                    ) : (
                        <p className="mt-1.5 text-sm sm:text-base text-ink-muted leading-snug">
                            יום טוב לגלות משהו חדש בקהילה.
                        </p>
                    )}
                </div>
            </div>
        </motion.section>
    );
}

export default WelcomeHero;
